/**
 * PEOPLE (ADR-0021 §9 part 1, migration 0034) — who is in the firm or team,
 * what each may do, and how someone joins or leaves.
 *
 * Authority is shown as words, never as a title: who reviews and signs, who
 * invites and grants. Whether this viewer may change any of it is the
 * server's answer (`viewerMayManage`); every act states its reason, because
 * each is filed with the record.
 *
 * An invitation is a LINK, shown here ONCE after it is made: the person who
 * invites sends it themselves. Leveri keeps only a fingerprint of it.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { AuthorityGrantResponse, InviteMemberResponse, TeamResponse, type TeamMember } from "@leveri/contracts";
import { Button, Field, Notice, Reading, RuledList, Section, SectionHead, Select, TextField } from "../file/index.js";
import { call, failureMessage } from "../api.js";

const ROLE_WORDS: Record<TeamMember["role"], string> = { PARTNER: "Partner", MANAGER: "Manager", STAFF: "Staff" };

type Act = { memberId: string; kind: "GIVE_REVIEW" | "WITHDRAW_REVIEW" | "REMOVE" } | { invitationId: string; kind: "WITHDRAW_INVITATION" };
const actKey = (a: Act) => ("memberId" in a ? `${a.kind}:${a.memberId}` : `${a.kind}:${a.invitationId}`);

export function TeamSection() {
  const [team, setTeam] = useState<TeamResponse | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [open, setOpen] = useState<string | null>(null);
  const [link, setLink] = useState<{ email: string; url: string } | null>(null);

  const load = useCallback(async () => {
    try { setTeam(await call("GET", "/api/firm/team", TeamResponse)); } catch (error) { setFailure(failureMessage(error)); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  if (failure) return <Section><SectionHead>People</SectionHead><Notice>{failure}</Notice></Section>;
  if (!team) return <Section><SectionHead>People</SectionHead><Reading>Reading who is in the team…</Reading></Section>;

  return (
    <Section>
      <SectionHead aside={`${team.members.length} ${team.members.length === 1 ? "person" : "people"}`}>People</SectionHead>
      <RuledList>
        {team.members.map((m) => {
          const powers = [
            m.review === "FIRM" ? "Reviews and signs the work" : m.review === "ENGAGEMENTS" ? "Reviews some engagements" : null,
            m.grantsAuthority ? "Invites people and grants authority" : null,
          ].filter(Boolean).join(" · ");
          const acts: Act[] = team.viewerMayManage && !m.you
            ? [m.review === "FIRM" ? { memberId: m.memberId, kind: "WITHDRAW_REVIEW" } : { memberId: m.memberId, kind: "GIVE_REVIEW" }, { memberId: m.memberId, kind: "REMOVE" }]
            : [];
          return (
            <div key={m.memberId} className="py-3" data-testid={`member-${m.email}`}>
              <div className="grid grid-cols-1 gap-x-6 gap-y-0.5 md:grid-cols-[minmax(0,1fr)_minmax(0,1.3fr)]">
                <div className="min-w-0">
                  <div className="truncate text-[15px] text-ink">{m.displayName}{m.you && <span className="text-stone"> (you)</span>}</div>
                  <div className="truncate text-[13px] text-stone">{ROLE_WORDS[m.role]} · {m.email}</div>
                </div>
                <div className="min-w-0 text-[13px] text-ink-soft md:text-right">
                  {powers || <span className="text-stone">Prepares work</span>}
                  {acts.length > 0 && (
                    <div className="mt-0.5 flex flex-wrap gap-x-3 md:justify-end">
                      {acts.map((a) => (
                        <Button key={actKey(a)} variant="ghost" size="sm" className="h-auto px-0" onClick={() => setOpen(open === actKey(a) ? null : actKey(a))} data-testid={`${a.kind.toLowerCase()}-${m.email}`}>
                          {a.kind === "GIVE_REVIEW" ? "Give review authority" : a.kind === "WITHDRAW_REVIEW" ? "Withdraw review authority" : "Remove"}
                        </Button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {acts.filter((a) => open === actKey(a)).map((a) => (
                <ReasonForm key={actKey(a)} act={a} member={m} onClose={() => setOpen(null)} onDone={async () => { setOpen(null); await load(); }} />
              ))}
            </div>
          );
        })}
      </RuledList>

      {team.viewerMayManage ? (
        <InviteForm onInvited={(next, sent) => { setTeam(next.team); setLink({ email: sent, url: `${window.location.origin}/join#${next.token}` }); }} />
      ) : (
        <p className="mt-4 text-[13px] text-stone">A member who can grant authority invites people to the team.</p>
      )}

      {link && (
        <div className="mt-4 border-l-2 border-forest pl-4" data-testid="invitation-link">
          <p className="text-[13px] text-ink">Send this link to {link.email}. It is shown once, works once, and expires in 7 days.</p>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <input readOnly value={link.url} onFocus={(e) => e.currentTarget.select()} aria-label="Invitation link"
              className="h-[34px] min-w-0 flex-1 rounded-control border border-line-strong bg-raised px-2.5 font-mono text-[12px] text-ink" />
            <Button variant="secondary" size="sm" onClick={() => void navigator.clipboard?.writeText(link.url)}>Copy link</Button>
            <Button variant="ghost" size="sm" onClick={() => setLink(null)}>Done</Button>
          </div>
        </div>
      )}

      {team.invitations.length > 0 && (
        <div className="mt-6">
          <div className="mb-1 text-[13px] font-medium text-stone">Invited, not yet joined</div>
          <RuledList>
            {team.invitations.map((i) => {
              const act: Act = { invitationId: i.id, kind: "WITHDRAW_INVITATION" };
              return (
                <div key={i.id} className="py-2.5" data-testid={`invitation-${i.email}`}>
                  <div className="flex flex-wrap items-baseline justify-between gap-x-4">
                    <div className="min-w-0">
                      <span className="text-[15px] text-ink">{i.email}</span>
                      <span className="text-[13px] text-stone"> · {ROLE_WORDS[i.role]} · invited by {i.invitedByName}</span>
                    </div>
                    <div className="text-[13px] text-stone">
                      {i.state === "OPEN" ? `Open until ${i.expiresAtText}` : "Expired"}
                      {team.viewerMayManage && (
                        <Button variant="ghost" size="sm" className="ml-3 h-auto px-0" onClick={() => setOpen(actKey(act))}>Withdraw</Button>
                      )}
                    </div>
                  </div>
                  {open === actKey(act) && <ReasonForm act={act} member={null} onClose={() => setOpen(null)} onDone={async () => { setOpen(null); await load(); }} />}
                </div>
              );
            })}
          </RuledList>
        </div>
      )}
    </Section>
  );
}

function InviteForm({ onInvited }: { onInvited: (next: InviteMemberResponse, email: string) => void }) {
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  async function invite(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    const formEl = event.currentTarget;
    const form = new FormData(formEl);
    const email = String(form.get("email") ?? "").trim();
    setSaving(true);
    setFailure(null);
    try {
      onInvited(await call("POST", "/api/firm/team/invitations", InviteMemberResponse, { email, role: String(form.get("role") ?? "STAFF") }), email);
      formEl.reset();
    } catch (error) {
      setFailure(failureMessage(error));
    } finally {
      setSaving(false);
    }
  }
  return (
    <form onSubmit={invite} className="mt-5 grid grid-cols-1 items-end gap-3 md:grid-cols-[minmax(0,1fr)_160px_auto]" data-testid="invite-form">
      <TextField name="email" type="email" label="Invite someone" placeholder="name@firm.com" required disabled={saving} />
      <Select name="role" label="As" defaultValue="STAFF" disabled={saving}>
        <option value="STAFF">Staff</option>
        <option value="MANAGER">Manager</option>
        <option value="PARTNER">Partner</option>
      </Select>
      <Button type="submit" disabled={saving}>Invite</Button>
      {failure && <div className="md:col-span-3"><Notice>{failure}</Notice></div>}
    </form>
  );
}

/** Every act on a person or an invitation states its reason; it is filed with the record. */
function ReasonForm({ act, member, onClose, onDone }: { act: Act; member: TeamMember | null; onClose: () => void; onDone: () => Promise<void> }) {
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const words = {
    GIVE_REVIEW: { label: "Why they may review and sign", button: "Give review authority" },
    WITHDRAW_REVIEW: { label: "Why it is withdrawn", button: "Withdraw review authority" },
    REMOVE: { label: "Why they are leaving", button: `Remove ${member?.displayName ?? ""}` },
    WITHDRAW_INVITATION: { label: "Withdraw this invitation?", button: "Withdraw the invitation" },
  }[act.kind];
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (saving) return;
    const reason = String(new FormData(event.currentTarget).get("reason") ?? "").trim();
    setSaving(true);
    setFailure(null);
    try {
      if (act.kind === "WITHDRAW_INVITATION") await call("POST", `/api/firm/team/invitations/${act.invitationId}/revoke`, TeamResponse);
      else if (act.kind === "REMOVE") await call("POST", `/api/firm/team/members/${act.memberId}/remove`, TeamResponse, { reason });
      else if (act.kind === "GIVE_REVIEW") await call("POST", "/api/firm/authority", AuthorityGrantResponse, { memberId: act.memberId, authority: "ACCEPT_CONCLUSIONS", scope: { kind: "FIRM" }, reason });
      else if (member?.reviewGrantId) await call("POST", `/api/firm/authority/${member.reviewGrantId}/revoke`, AuthorityGrantResponse, { reason });
      await onDone();
    } catch (error) {
      setFailure(failureMessage(error));
      setSaving(false);
    }
  }
  return (
    <form onSubmit={submit} className="mt-3 grid gap-3" data-testid={`reason-${act.kind.toLowerCase()}`}>
      {act.kind !== "WITHDRAW_INVITATION" && (
        <Field label={words.label} hint="Filed with the record.">
          <textarea name="reason" rows={2} required disabled={saving}
            className="w-full rounded-control border border-line-strong bg-canvas px-3 py-2 text-sm text-ink focus:border-brand" />
        </Field>
      )}
      <div className="flex flex-wrap items-center gap-3">
        <Button type="submit" variant="secondary" disabled={saving}>{words.button}</Button>
        <Button type="button" variant="ghost" disabled={saving} onClick={onClose}>Cancel</Button>
      </div>
      {failure && <Notice>{failure}</Notice>}
    </form>
  );
}
